import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { getOptionsQueryParams, getValuesFromEnum } from "@app/class/Utilities";
import { IVerticalFilterListItem } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { IBasicProductModel } from "@app/interfaces/IProductUiViewModel";
import { ProductVendor } from "@app/main/components/videos/enums/CameraProductType";
import { IAccount } from "@app/services/account.service";
import { ProductsService } from "@app/services/products.service";
import { TranslationService } from "@app/services/translation.service";
import { Observable, combineLatest, firstValueFrom, forkJoin, of } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";
import { AccountService } from "../../_services/account.service";
import { DeviceHealthRecordType } from "./enums/device-health-record-type";
import { DeviceHealthRecordHelper } from "./helpers/device-health-record-helper";
import { DeviceHealthRecord } from "./models/device-health-record";
import { DeviceHealthRecordQueryOptions } from "./models/device-health-record-query-options";
import { DeviceHealthRecordsByVehicle } from "./models/device-health-records-by-vehicle";
import { DeviceHealthSimpleSummary, DeviceHealthTypeSummary } from "./models/device-health-simple-summary";
import { DeviceHealthVehicleDurationSummary } from "./models/device-health-vehicle-duration-summary";
import { IMinMaxValues } from "./models/minMaxValues";

@Injectable({
    providedIn: 'root'
})
export class DeviceHealthService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly translationService: TranslationService,
        private readonly productsService: ProductsService,
        private readonly accountService: AccountService,
    ) { }

    getRecords(queryOptions: DeviceHealthRecordQueryOptions = {}): Observable<DeviceHealthRecord[]> {
        const options = getOptionsQueryParams(queryOptions);
        return this.httpClient.get<DeviceHealthRecord[]>('/api/devicehealth/records', options);
    }

    getSummariesWithCompany(): Observable<DeviceHealthVehicleDurationSummary[]> {
        return combineLatest([
            this.accountService.getCustomerAccounts(),
            this.getAllSummaries()
        ]).pipe(map(([accounts, summaries]) => {
            return this.combineAndGetSummariesWithAccounts(accounts, summaries);
        }));
    } 

    getAllSummaries(): Observable<DeviceHealthSimpleSummary[]> {
        return this.httpClient.get<DeviceHealthSimpleSummary[]>('/api/devicehealth/summaries');
    }

    getAllActiveAndHistoricalRecords(vehicleId: number, startDate?: Date, endDate?: Date): Observable<DeviceHealthRecordsByVehicle> {
        return forkJoin({
            records:  this.getRecords({ VehicleId: vehicleId, Start: startDate, End: endDate, IncludeAllIntersecting: true }), 
            activeRecords: this.getRecords({ VehicleId: vehicleId, Active: true }), 
        }).pipe(
            map(data => {
                const mergedRecords = data.records;

                data.activeRecords.forEach(activeRecord => {
                    const recordAlreadyInArray = mergedRecords.some(m => m.Type === activeRecord.Type && m.Serial === activeRecord.Serial);
    
                    if (!recordAlreadyInArray) {
                        mergedRecords.push(activeRecord);
                    }
                })

                return mergedRecords;
            }),
            filter(mergedRecords => mergedRecords.length > 0),
            switchMap(mergedRecords => {
                return forkJoin({
                    products: this.productsService.getProductUiViewModels(), 
                    account: this.accountService.getCustomerAccount(mergedRecords[0].AccountId),
                    records: of(mergedRecords)
                })
            }),
            map(data => {
                const records = this.mapProductNameToRecords(data.records, data.products);
                return new DeviceHealthRecordsByVehicle(records, data.account);  
            })
        )
    }

    private getProperRecordTypesFromSummary(
        summary: DeviceHealthVehicleDurationSummary, 
        type: 'telematics' | 'camera'
    ): DeviceHealthRecordType[] {
        switch (type) {
            case 'camera':
                return summary.cameraRecordTypes;
            case 'telematics':
                return summary.telematicsRecordTypes;
        }
    }

    getRecordTypeFiltersForVehicleSummaries(
        data: DeviceHealthVehicleDurationSummary[], 
        type: 'telematics' | 'camera'
    ): IVerticalFilterListItem<DeviceHealthVehicleDurationSummary>[] {
        const filters: IVerticalFilterListItem<DeviceHealthVehicleDurationSummary>[] = [];

        const enumValues = getValuesFromEnum(DeviceHealthRecordType)
            .filter(value => value != DeviceHealthRecordType.None);

        enumValues.forEach(value => {
            if (!data.some(summary => this.getProperRecordTypesFromSummary(summary, type).includes(value))) {
                return;
            }

            const filter: IVerticalFilterListItem<DeviceHealthVehicleDurationSummary> = {
                name: this.translateRecordType(value),
                predicate: (summary) => {
                    return this.getProperRecordTypesFromSummary(summary, type).includes(value);
                },
            };

            filter.count = data.filter(d => filter.predicate(d)).length;
            filter.key = value;
            
            filters.push(filter);
        })

        return filters;
    }

    async getResellerFiltersForVehicleSummaries(
        data: DeviceHealthVehicleDurationSummary[]
    ): Promise<IVerticalFilterListItem<DeviceHealthVehicleDurationSummary>[]> {
        const accounts = await firstValueFrom(this.accountService.getCustomerAccounts());
        const resellers = await firstValueFrom(this.accountService.getResellers());

        const accountsIdsWithReseller = accounts
            .filter(x => !!x.ResellerId)
            .map(x => x.Id);


        const filters: IVerticalFilterListItem<DeviceHealthVehicleDurationSummary>[] = [];

        resellers.forEach(reseller => {
            const resellerAccounts = accounts.filter(account => account.ResellerId === reseller.Id);

            const isResellerPresent = data.some(summary => 
                resellerAccounts.some(account => account.Id === summary.accountId)
            );

            if (!isResellerPresent) {
                return;
            }

            const filter: IVerticalFilterListItem<DeviceHealthVehicleDurationSummary> = {
                name: reseller.Description,
                predicate: (summary) => {
                    if (!accountsIdsWithReseller.includes(summary.accountId)) {
                        return false;
                    }

                    const account = accounts.find(x => x.Id === summary.accountId)!;
                    const summaryReseller = resellers.find(x => x.Id === account.ResellerId);

                    return summaryReseller.Id === reseller.Id;
                },
            };

            filter.count = data.filter(d => filter.predicate(d)).length;
            filter.key = reseller.Id;
            
            filters.push(filter);
        });

        return filters;
    }

    getFiltersForVehicleDetails(records: DeviceHealthRecord[]): IVerticalFilterListItem<DeviceHealthRecord>[] {
        const filters: IVerticalFilterListItem<DeviceHealthRecord>[] = [];

        const enumValues = getValuesFromEnum(DeviceHealthRecordType)
            .filter(value => value != DeviceHealthRecordType.None);

        enumValues.forEach(value => {
            if (!records.some(record =>  record.Type === value)) {
                return;
            }

            const filter: IVerticalFilterListItem<DeviceHealthRecord> = {
                name: this.translateRecordType(value),
                predicate: (record) => record.Type === value,
            };

            filter.count = records.filter(d => filter.predicate(d)).length;
            filter.key = value;
            
            filters.push(filter);
        });

        return filters;
    }

    getScoreForRecord(record: DeviceHealthVehicleDurationSummary, minMaxValues: IMinMaxValues): number {
        const { minRecords, maxRecords, minDuration, maxDuration } = minMaxValues;
        const normalizedRecords = (record.recordsCount - minRecords) / (maxRecords - minRecords);
        const normalizedDuration = (record.totalDuration - minDuration) / (maxDuration - minDuration);
        return 0.5 * normalizedRecords + 0.5 * normalizedDuration;
    }

    getMinAndMaxValues(vehicles: DeviceHealthVehicleDurationSummary[]): IMinMaxValues {
        if (!vehicles.length) {
            return;
        }

        return vehicles.reduce((acc, vehicle) => {
            acc.minRecords = Math.min(acc.minRecords, vehicle.recordsCount);
            acc.maxRecords = Math.max(acc.maxRecords, vehicle.recordsCount);
            acc.minDuration = Math.min(acc.minDuration, vehicle.totalDuration);
            acc.maxDuration = Math.max(acc.maxDuration, vehicle.totalDuration);
            return acc;
        }, {
            minRecords: Infinity,
            maxRecords: -Infinity,
            minDuration: Infinity,
            maxDuration: -Infinity
        });
    }

    private mapProductNameToRecords(records: DeviceHealthRecord[], products: IBasicProductModel[]): DeviceHealthRecord[] {
        return records.map(record => {
            const product = products.find(product => product.ProductId === record.ProductId);

            const productName = product
                ? `${ProductVendor[product?.GroupId]} ${product?.Description}`
                : null;

            return { ...record, productName };
        })
    }

    private combineAndGetSummariesWithAccounts(
        accounts: IAccount[], 
        summaries: DeviceHealthSimpleSummary[]  
    ): DeviceHealthVehicleDurationSummary[] {
        const accountsMap = this.accountService.createAccountMap(accounts);
        const summariesByVehicle = this.groupSummariesByVehicleId(summaries);

        const result: DeviceHealthVehicleDurationSummary[] = Object
            .keys(summariesByVehicle)
            .map((key) => {
                const summaries = summariesByVehicle[Number(key)];
                const summaryResult = this.buildDeviceHealthVehicleDurationSummary(accountsMap, summaries);

                return summaryResult;
            });

        return this.getVehicleSummariesWithScores(result);
    }

    private buildDeviceHealthVehicleDurationSummary(
        accountsMap: Map<number, IAccount>,
        summaries: DeviceHealthSimpleSummary[]
    ): DeviceHealthVehicleDurationSummary {
        const firstSummary = summaries[0]; // to get props that are the same for all
        const allDaySummaries = summaries.map(x => x.DaySummaries).flat();
        const allTypeSummaries = allDaySummaries.map(x => x.TypeSummaries).flat();

        const account = accountsMap.get(firstSummary.AccountId);
        const recordsCount = this.getRecordsCountFromAllDays(allTypeSummaries);

        const cameraRecordTypes = this.getRecordTypes(allTypeSummaries, 'camera');
        const cameraRecordTypesDesc = this.getRecordTypesDesc(cameraRecordTypes);

        const telematicsRecordTypes = this.getRecordTypes(allTypeSummaries, 'telematics');
        const telematicsRecordTypesDesc = this.getRecordTypesDesc(telematicsRecordTypes);

        const totalDuration = this.getTotalDuration(allTypeSummaries);

        return {
            vehicleId: firstSummary.VehicleId,
            reg: firstSummary.VehicleRegistration,
            accountId: firstSummary.AccountId,
            companyName: account?.CompanyName,
            recordsCount,
            cameraRecordTypes,
            cameraRecordTypesDesc,
            telematicsRecordTypes,
            telematicsRecordTypesDesc,
            totalDuration,
            score: 0,
            serial: firstSummary.Serial
        }
    }

    private groupSummariesByVehicleId(
        summaries: DeviceHealthSimpleSummary[]
    ): { [key: number]: DeviceHealthSimpleSummary[] } {
        const summariesByVehicle = summaries.reduce((acc, current) => {
            if (!current.VehicleId) {
                return acc;
            }

            const vehicleId = current.VehicleId;

            acc[vehicleId] = acc[vehicleId] || [];
            acc[vehicleId].push(current);

            return acc;
        }, {} as {[key: number]: DeviceHealthSimpleSummary[]})

        return summariesByVehicle;
    }

    private getVehicleSummariesWithScores(vehicles: DeviceHealthVehicleDurationSummary[]): DeviceHealthVehicleDurationSummary[] {
        const { minRecords, maxRecords, minDuration, maxDuration } = this.getMinAndMaxValues(vehicles);

        vehicles.forEach(vehicle => {

            const normalizedRecords = (vehicle.recordsCount - minRecords) / (maxRecords - minRecords);
            const normalizedDuration = (vehicle.totalDuration - minDuration) / (maxDuration - minDuration);
            vehicle.score = 0.5 * normalizedRecords + 0.5 * normalizedDuration;
        });

        return vehicles;
    }

    private getTotalDuration(allTypeSummaries: DeviceHealthTypeSummary[]):number {
        const durations = allTypeSummaries.map(summary => summary.DurationSec);

        return durations.reduce((acc, current) => {
            return acc + current
        }, 0);
    }

    private getRecordTypesDesc(recordTypes: DeviceHealthRecordType[]): string {
        return recordTypes
            .sort()
            .map(recordType => {
                return this.translateRecordType(recordType)
            }).join(', ');
    }

    private getRecordsCountFromAllDays(allTypeSummaries: DeviceHealthTypeSummary[]): number {
        return allTypeSummaries.map(summary => summary.Count).reduce((acc, current) => {
            return acc + current;
        }, 0);
    }

    private getRecordTypes(allTypeSummaries: DeviceHealthTypeSummary[], type: 'telematics' | 'camera'): DeviceHealthRecordType[] {
        const recordTypesUnique = new Set(allTypeSummaries.map(x => x.Type));
        const result = [...recordTypesUnique];

        switch (type) {
            case 'camera':
                return result.filter(x => DeviceHealthRecordHelper.isCamera(x));
            case 'telematics':
                return result.filter(x => DeviceHealthRecordHelper.isTelematic(x));
        }
    }

    private translateRecordType(recordType: DeviceHealthRecordType): string {
        return this.translationService.tEnum(DeviceHealthRecordType, "DeviceHealthRecordType", recordType);
    }
}
